import {
  action,
  computed,
  makeObservable,
  observable,
} from "mobx";

import { normalizeFoodClassification } from "../../nutrition/foods/foodClassification";
import {
  createInitialEquivalentFormState,
  getEquivalentDraftValidity,
  getEquivalentOptionsForSave,
} from "../../nutrition/equivalents/equivalentLogic";
import {
  assertNutritionLimit,
  validateEquivalentDraftLimits,
} from "../../nutrition/nutritionLimits";
import {
  getIngredientTotals,
} from "../../nutrition/recipes/recipeLogic";
import OptimisticNutritionEntity, {
  buildComparablePayload,
  NutritionEntitySaveMode,
  clonePlainValue,
  getOptimisticEntityAnnotations,
} from "./OptimisticNutritionEntity";
import { createEmptyTotals } from "./NutritionItems";

function hasText(value) {
  return typeof value === "string" && value.trim().length > 0;
}

function fallbackTranslate(_key, fallbackValue) {
  return fallbackValue ?? "";
}

function buildEquivalentDraft(equivalentLike) {
  return createInitialEquivalentFormState(equivalentLike, fallbackTranslate);
}

export class NutritionEquivalent extends OptimisticNutritionEntity {
  entityType = "equivalent";
  id = "";
  origin = null;
  createdAt = null;
  updatedAt = null;
  draft = buildEquivalentDraft({});

  constructor(data = {}, options = {}) {
    super({
      store: options.store,
      saveMode: NutritionEntitySaveMode.DEBOUNCED,
      saveDelayMs: 1_200,
    });

    const draft = buildEquivalentDraft(data);
    this.id = String(data?.id ?? "").trim().toLowerCase();
    this.origin = data?.origin ?? null;
    this.createdAt = data?.createdAt ?? null;
    this.updatedAt = data?.updatedAt ?? null;
    this.draft = draft;
    this.initializeFromServerSnapshot(draft, { rev: data?._rev });

    makeObservable(this, {
      ...getOptimisticEntityAnnotations(),
      entityType: observable,
      id: observable,
      origin: observable.ref,
      createdAt: observable.ref,
      updatedAt: observable.ref,
      draft: observable.ref,
      name: computed,
      options: computed,
      sortedOptions: computed,
      optionsPreview: computed,
      foods: computed,
      referenceTotals: computed,
      calories: computed,
      carbs: computed,
      protein: computed,
      fats: computed,
      allergenCodes: computed,
      isVegetarian: computed,
      isVegan: computed,
      isClassificationComplete: computed,
      isValid: computed,
      searchableText: computed,
      replaceDraftSnapshot: action.bound,
      applyServerMetadata: action.bound,
      applyServerRecord: action.bound,
      tryApplyNoRefreshServerAck: action.bound,
      applyDraft: action.bound,
    });
  }

  static fromRecord(record, options = {}) {
    return new NutritionEquivalent(record, options);
  }

  static buildDraftFromRecord(record) {
    return buildEquivalentDraft(record);
  }

  static buildRecordFromDraft(draft, metadata = {}) {
    const record = {
      schema_version: "nutrition_equivalent_v1",
      id: String(metadata?.id ?? "").trim().toLowerCase(),
      name: String(draft?.name ?? "").trim(),
      options: getEquivalentOptionsForSave(draft?.options),
      ...(metadata?.origin ? { origin: metadata.origin } : {}),
      ...(metadata?.rev ? { _rev: Number(metadata.rev) } : {}),
      ...(metadata?.createdAt !== undefined ? { createdAt: metadata.createdAt } : {}),
      ...(metadata?.updatedAt !== undefined ? { updatedAt: metadata.updatedAt } : {}),
    };

    assertNutritionLimit(validateEquivalentDraftLimits(record));
    return record;
  }

  applyServerRecord(record) {
    this.applyServerMetadata(record);
    this.mergeServerSnapshot(this.buildDraftFromServerRecord(record), {
      rev: record?._rev,
    });
  }

  applyServerMetadata(record) {
    this.origin = record?.origin ?? null;
    this.createdAt = record?.createdAt ?? this.createdAt ?? null;
    this.updatedAt = record?.updatedAt ?? this.updatedAt ?? null;
  }

  buildDraftFromServerRecord(record) {
    return buildEquivalentDraft(record);
  }

  buildComparablePersistedPayloadFromDraft(draft = this.draft, options = {}) {
    return buildComparablePayload(
      NutritionEquivalent.buildRecordFromDraft(draft, {
        id: options.id ?? this.id,
        origin: options.origin ?? this.origin,
        createdAt: options.createdAt ?? this.createdAt,
        updatedAt: options.updatedAt ?? this.updatedAt,
        rev: options.rev ?? this._rev,
      }),
    );
  }

  buildComparablePersistedPayloadFromServer(record) {
    return this.buildComparablePersistedPayloadFromDraft(
      NutritionEquivalent.buildDraftFromRecord(record),
      {
        id: record?.id ?? this.id,
        origin: record?.origin ?? null,
        createdAt: record?.createdAt,
        updatedAt: record?.updatedAt,
        rev: record?._rev,
      },
    );
  }

  tryApplyNoRefreshServerAck(record, options = {}) {
    const comparablePayload = this.buildComparablePersistedPayloadFromServer(record);
    if (comparablePayload == null) {
      return false;
    }

    this.applyServerMetadata(record);
    return this.tryProcessComparableServerAck({
      nextSnapshot: this.buildDraftFromServerRecord(record),
      nextRev: record?._rev,
      comparablePayload,
      finalize: options.finalizePendingAck !== false,
    });
  }

  get isPersistable() {
    return getEquivalentDraftValidity(this.draft).canSave;
  }

  getDraftSnapshot() {
    return clonePlainValue(this.draft);
  }

  replaceDraftSnapshot(snapshot) {
    this.draft = clonePlainValue(snapshot ?? buildEquivalentDraft({}));
  }

  get name() {
    return String(this.draft?.name ?? "");
  }

  get options() {
    return Array.isArray(this.draft?.options) ? this.draft.options : [];
  }

  get sortedOptions() {
    return [...this.options].sort((leftOption, rightOption) =>
      String(leftOption?.name ?? "").localeCompare(String(rightOption?.name ?? ""), undefined, {
        sensitivity: "base",
      }),
    );
  }

  get optionsPreview() {
    return this.sortedOptions.slice(0, 3).map((option) => option.name);
  }

  get foods() {
    return this.optionsPreview;
  }

  get referenceTotals() {
    const firstOption = this.options[0];
    return firstOption ? getIngredientTotals(firstOption) : createEmptyTotals();
  }

  get calories() {
    return this.referenceTotals.calories;
  }

  get carbs() {
    return this.referenceTotals.carbs;
  }

  get protein() {
    return this.referenceTotals.protein;
  }

  get fats() {
    return this.referenceTotals.fats;
  }

  get allergenCodes() {
    return Array.from(
      new Set(this.options.flatMap((option) => normalizeFoodClassification(option).allergen_codes)),
    );
  }

  get isVegetarian() {
    return this.options.length > 0 && this.options.every((option) => option.isVegetarian === true);
  }

  get isVegan() {
    return this.options.length > 0 && this.options.every((option) => option.isVegan === true);
  }

  get isClassificationComplete() {
    return (
      this.options.length > 0 &&
      this.options.every(
        (option) =>
          option.allergenDataComplete === true &&
          option.dietaryDataComplete === true,
      )
    );
  }

  get isValid() {
    return getEquivalentDraftValidity(this.draft).canSave;
  }

  get searchableText() {
    return [this.name, ...this.options.map((option) => option.name)]
      .filter(hasText)
      .join(" ")
      .toLowerCase();
  }

  applyDraft(nextDraft, options = {}) {
    this.applyDraftSnapshot(nextDraft, options);
  }

  buildPersistableRecord(options = {}) {
    return {
      schema_version: "nutrition_equivalent_v1",
      id: this.id,
      name: this.name.trim(),
      options: getEquivalentOptionsForSave(this.options),
      ...(this.origin ? { origin: this.origin } : {}),
      ...(options.rev ?? this._rev ? { _rev: Number(options.rev ?? this._rev) } : {}),
      ...(options.createdAt !== undefined
        ? { createdAt: options.createdAt ?? this.createdAt }
        : {}),
      ...(options.updatedAt !== undefined
        ? { updatedAt: options.updatedAt ?? this.updatedAt }
        : {}),
    };
  }

  toFirestore(options = {}) {
    return this.buildPersistableRecord(options);
  }
}

export default NutritionEquivalent;
